// SPDX-License-Identifier: GPL-3.0-only
// Copyright (C) 2026 Kyle Johnson
// This file is part of Hushold.
//
// Hushold is free software: you can redistribute it and/or modify
// it under the terms of the GNU General Public License as
// published by the Free Software Foundation, version 3.

// Event types matching the core package (packages/core/src/types.ts).
// Duplicated here to avoid a build-time dependency on the core package from the browser bundle.

export type AccountType =
  | 'ASSET'
  | 'BANK'
  | 'CASH'
  | 'CREDIT_CARD'
  | 'EQUITY'
  | 'EXPENSE'
  | 'INCOME'
  | 'LIABILITY'
  | 'MUTUAL_FUND'
  | 'STOCK'
  | 'ACCOUNTS_RECEIVABLE'
  | 'ACCOUNTS_PAYABLE'
  | 'ROOT'
  | 'TRADING'
  // Legacy types — kept for backwards compatibility with existing encrypted data
  | 'CREDIT'
  | 'INVESTMENT'
  | 'RECEIVABLE'
  | 'PAYABLE';

export type ReconcileState = 'n' | 'c' | 'y'; // none, cleared, reconciled

// --- Shared sub-types ---

export interface Commodity {
  ticker: string;
  exchange: string;
  fullName: string;
}

export interface SplitTemplate {
  splitId: string;
  accountId: string;
  amount: number;
  quantity: number;
  memo: string;
  action: string;
}

export interface TransactionTemplate {
  description: string;
  num: string;
  notes: string;
  currency: string;
  splits: SplitTemplate[];
}

export interface FrequencySpec {
  type: 'once' | 'daily' | 'weekly' | 'biweekly' | 'monthly' | 'quarterly' | 'yearly';
  interval: number;
  startDate: string;
  endDate: string | null;
  lastOccurrence: string | null;
  nextOccurrence: string | null;
}

export interface BudgetPeriodValue {
  accountId: string;
  period: number; // 1-12 for monthly, 1-4 for quarterly
  amount: number; // in minor units
}

// --- Event payloads ---

export interface AccountCreatedPayload {
  accountId: string;
  name: string;
  type: AccountType;
  currency: string;
  parentId: string | null;
  description: string;
  code?: string;
  hidden?: boolean;
  placeholder: boolean;
  commodity?: Commodity;
  openingBalance?: number;
  openingBalanceDate?: string;
}

export interface AccountUpdatedPayload {
  accountId: string;
  name?: string;
  description?: string;
  code?: string;
  hidden?: boolean;
  placeholder?: boolean;
  parentId?: string | null;
  commodity?: Commodity;
}

export interface AccountDeletedPayload {
  accountId: string;
  reparentChildrenToId?: string | null;
}

export interface Split {
  splitId: string;
  accountId: string;
  amount: number; // in minor units of the account's currency (e.g. cents)
  quantity: number; // for investment accounts; equals amount for cash
  value?: number; // equivalent in the transaction's reporting currency (for multi-currency)
  reconcileState: ReconcileState;
  reconcileDate: string | null;
  memo: string;
  action: string;
}

export interface TransactionPostedPayload {
  transactionId: string;
  date: string;
  dateEntered?: string;
  description: string;
  num?: string;
  notes: string;
  currency: string;
  splits: Split[];
  fitid?: string; // financial institution transaction ID for import duplicate detection
  importSessionId?: string; // groups transactions from a single import
}

export interface TransactionVoidedPayload {
  transactionId: string;
  voidReason: string;
}

export interface TransactionDeletedPayload {
  transactionId: string;
}

export interface SplitReconciledPayload {
  transactionId: string;
  splitId: string;
  accountId: string;
  reconcileState: ReconcileState;
  reconcileDate: string | null;
}

export interface ReconciliationPostedPayload {
  accountId: string;
  statementDate: string;
  statementBalance: number; // in minor units
  reconciledSplitIds: string[];
}

export interface ReconciliationStartedPayload {
  accountId: string;
  statementDate: string;
  statementBalance: number;
}

export interface ReconciliationCompletedPayload {
  accountId: string;
  statementDate: string;
  statementBalance: number;
  reconciledSplitIds: string[];
}

export interface ScheduledTransactionCreatedPayload {
  scheduledTransactionId: string;
  name: string;
  enabled: boolean;
  autoCreate: boolean;
  autoCreateDaysInAdvance: number;
  advanceReminderDays: number;
  templateTransaction: TransactionTemplate;
  frequency: FrequencySpec;
}

export interface ScheduledTransactionUpdatedPayload {
  scheduledTransactionId: string;
  name?: string;
  enabled?: boolean;
  autoCreate?: boolean;
  autoCreateDaysInAdvance?: number;
  advanceReminderDays?: number;
  templateTransaction?: TransactionTemplate;
  frequency?: FrequencySpec;
}

export interface ScheduledTransactionDeletedPayload {
  scheduledTransactionId: string;
}

export interface BudgetCreatedPayload {
  budgetId: string;
  name: string;
  description: string;
  periodType: 'monthly' | 'quarterly' | 'yearly';
  numPeriods: number;
  startDate: string;
  values: BudgetPeriodValue[];
}

export interface BudgetUpdatedPayload {
  budgetId: string;
  name?: string;
  description?: string;
  values?: BudgetPeriodValue[];
}

export interface BudgetDeletedPayload {
  budgetId: string;
}

export interface PriceAddedPayload {
  priceId: string;
  commodityId: string;
  currency: string;
  date: string;
  source: string;
  type: 'bid' | 'ask' | 'last' | 'nav' | 'transaction' | 'unknown';
  value: number;
}

export interface PriceUpdatedPayload {
  priceId: string;
  value?: number;
  date?: string;
  source?: string;
}

export interface PriceDeletedPayload {
  priceId: string;
}

export interface SettingsUpdatedPayload {
  homeCurrency?: string;
}

// ─── Settings System ──────────────────────────────────────────────────────────

export type SettingsKey =
  | 'homeCurrency'
  | 'dateFormat'
  | 'numberFormat'
  | 'language'
  | 'theme'
  | 'fontSize'
  | 'registerRowHeight'
  | 'costBasisMethod'
  | 'defaultCurrencyGainAccount'
  | 'defaultCommissionAccount'
  | 'scheduledTxLookAheadDays'
  | 'reconciliationThreshold'
  | 'showRunningBalance'
  | 'showReconcileColumn'
  | 'defaultSortOrder'
  | 'transactionJournalMode'
  | 'scheduledTxReminders'
  | 'reconciliationReminders'
  | 'monthlyReportEmail'
  | 'defaultQuickEntryAccount'
  | 'coinbaseCredential'
  | 'coinbaseAccountMapping'
  | 'autoPriceSync'
  | 'alphaVantageKey';

export interface CoinbaseCredential {
  apiKey: string;             // Coinbase API key name
  apiSecret: string;          // HMAC secret — stored encrypted in event log
  connectedAt: string;        // ISO datetime, when user first connected
  accountIds: string[];       // Coinbase account IDs synced
  lastSyncedAt: string | null;
  lastSyncTransactionCount: number;
}

export interface SettingsValues {
  homeCurrency: string;
  dateFormat: string;
  numberFormat: 'us' | 'eu';
  // UI language (BCP-47, e.g. 'en', 'de', 'zh-Hans'). '' = never explicitly set,
  // so the device-detected language (localStorage/navigator) is used and this
  // synced value does not override it. Set once the user picks a language.
  language: string;
  theme: 'dark' | 'light' | 'system';
  fontSize: 'sm' | 'md' | 'lg';
  registerRowHeight: 'compact' | 'normal' | 'comfortable';
  costBasisMethod: 'fifo' | 'average';
  defaultCurrencyGainAccount: string | null;
  defaultCommissionAccount: string | null;
  scheduledTxLookAheadDays: number;
  reconciliationThreshold: number;
  showRunningBalance: boolean;
  showReconcileColumn: boolean;
  defaultSortOrder: 'date-asc' | 'date-desc';
  transactionJournalMode: boolean;
  scheduledTxReminders: boolean;
  reconciliationReminders: boolean;
  monthlyReportEmail: boolean;
  // Default "From" account for mobile quick transaction entry (the user's
  // most-used cash/spending account). null = fall back to last-used account.
  defaultQuickEntryAccount: string | null;
  coinbaseCredential: CoinbaseCredential | null;
  coinbaseAccountMapping: Record<string, string>;
  // Automatic price sync (crypto/equity/FX via the sync-server price proxy).
  // Opt-in: off by default, in keeping with "no surprising network activity".
  autoPriceSync: boolean;
  // Alpha Vantage API key for stock/fund quotes (crypto and FX are keyless).
  // Stored encrypted in the event log, like the Coinbase credential; sent to
  // the price proxy in a header, never in a URL. null = equities not priced.
  alphaVantageKey: string | null;
}

export const SETTINGS_DEFAULTS: SettingsValues = {
  homeCurrency: 'USD',
  dateFormat: 'MM/DD/YYYY',
  numberFormat: 'us',
  language: '',
  theme: 'dark',
  fontSize: 'md',
  registerRowHeight: 'normal',
  costBasisMethod: 'fifo',
  defaultCurrencyGainAccount: null,
  defaultCommissionAccount: null,
  scheduledTxLookAheadDays: 7,
  reconciliationThreshold: 30,
  showRunningBalance: true,
  showReconcileColumn: true,
  defaultSortOrder: 'date-asc',
  transactionJournalMode: false,
  scheduledTxReminders: true,
  reconciliationReminders: true,
  monthlyReportEmail: false,
  defaultQuickEntryAccount: null,
  coinbaseCredential: null,
  coinbaseAccountMapping: {},
  autoPriceSync: false,
  alphaVantageKey: null,
};

export type SettingValue = SettingsValues[SettingsKey];

export interface SettingUpdatedPayload {
  key: SettingsKey;
  value: SettingValue;
  previousValue?: SettingValue;
}

export interface CredentialRevokedPayload {
  key: SettingsKey;
  reason: 'user_disconnected' | 'token_invalid' | 'refresh_failed';
  revokedAt: string;
}

export interface CredentialRefreshedPayload {
  key: SettingsKey;
  accessToken: string;
  expiresAt: string;
  refreshedAt: string;
  refreshedBy: string;
}

// --- Event types ---

export type EventType =
  | 'AccountCreated'
  | 'AccountUpdated'
  | 'AccountDeleted'
  | 'TransactionPosted'
  | 'TransactionVoided'
  | 'TransactionDeleted'
  | 'SplitReconciled'
  | 'ReconciliationPosted'
  | 'ReconciliationStarted'
  | 'ReconciliationCompleted'
  | 'ScheduledTransactionCreated'
  | 'ScheduledTransactionUpdated'
  | 'ScheduledTransactionDeleted'
  | 'BudgetCreated'
  | 'BudgetUpdated'
  | 'BudgetDeleted'
  | 'PriceAdded'
  | 'PriceUpdated'
  | 'PriceDeleted'
  | 'SettingsUpdated'
  | 'SettingUpdated'
  | 'CredentialRevoked'
  | 'CredentialRefreshed';

export type EventPayload =
  | AccountCreatedPayload
  | AccountUpdatedPayload
  | AccountDeletedPayload
  | TransactionPostedPayload
  | TransactionVoidedPayload
  | TransactionDeletedPayload
  | SplitReconciledPayload
  | ReconciliationPostedPayload
  | ReconciliationStartedPayload
  | ReconciliationCompletedPayload
  | ScheduledTransactionCreatedPayload
  | ScheduledTransactionUpdatedPayload
  | ScheduledTransactionDeletedPayload
  | BudgetCreatedPayload
  | BudgetUpdatedPayload
  | BudgetDeletedPayload
  | PriceAddedPayload
  | PriceUpdatedPayload
  | PriceDeletedPayload
  | SettingsUpdatedPayload
  | SettingUpdatedPayload
  | CredentialRevokedPayload
  | CredentialRefreshedPayload;

export interface LedgerEvent {
  id: string; // UUID v4
  sequence: number; // monotonically increasing, per-user
  type: EventType;
  timestamp: string; // ISO8601
  payload: EventPayload;
  checksum: string; // sha256 of (id + sequence + type + timestamp + JSON(payload))
}

// --- Ledger projection types ---

export interface Account {
  id: string;
  name: string;
  type: AccountType;
  currency: string;
  parentId: string | null;
  description: string;
  code: string;
  hidden: boolean;
  placeholder: boolean;
  commodity?: Commodity;
  balance: number; // in minor units, computed by replaying events
}

export interface Transaction {
  id: string;
  date: string;
  dateEntered: string;
  description: string;
  num: string;
  notes: string;
  currency: string;
  splits: Split[];
  voided: boolean;
  voidReason: string | null;
  fitid?: string; // financial institution transaction ID (from import)
}

export interface ScheduledTransaction {
  id: string;
  name: string;
  enabled: boolean;
  autoCreate: boolean;
  autoCreateDaysInAdvance: number;
  advanceReminderDays: number;
  templateTransaction: TransactionTemplate;
  frequency: FrequencySpec;
}

export interface Budget {
  id: string;
  name: string;
  description: string;
  periodType: 'monthly' | 'quarterly' | 'yearly';
  numPeriods: number;
  startDate: string;
  values: BudgetPeriodValue[];
}

export interface Price {
  id: string;
  commodityId: string;
  currency: string;
  date: string;
  source: string;
  type: 'bid' | 'ask' | 'last' | 'nav' | 'transaction' | 'unknown';
  value: number;
}

export interface LedgerSettings {
  homeCurrency: string;
}

export interface LedgerState {
  accounts: Map<string, Account>;
  transactions: Map<string, Transaction>;
  scheduledTransactions: Map<string, ScheduledTransaction>;
  budgets: Map<string, Budget>;
  prices: Map<string, Price>;
  settings: LedgerSettings;
  lastSequence: number;
}

// --- App-specific types ---

export interface AuthState {
  isAuthenticated: boolean;
  username: string | null;
  token: string | null; // short-lived access JWT
  refreshToken: string | null; // opaque refresh token, exchanged for new access tokens
  keyData: ArrayBuffer | null; // the DEK — raw AES-256 key bytes, in memory only, never serialized
  syncUrl: string;
  sessionExpired: boolean; // true when a stored token was found but was expired on load
  // Session restore is async (native Keychain/Keystore reads), so the reducer
  // starts unauthenticated and an effect resolves the stored session. Until then
  // `bootstrapped` is false and the app shows a neutral loading state rather than
  // flashing the login screen. On web this resolves in a microtask.
  bootstrapped: boolean;
  // Native only: a valid session exists in the Keychain but is gated behind a
  // biometric unlock. The app shows the lock screen instead of the login form.
  locked: boolean;
}

export interface SyncStatus {
  status: 'idle' | 'syncing' | 'error' | 'success';
  lastSyncAt: string | null;
  errorMessage: string | null;
}

export interface BlobEntry {
  sequence: number;
  blobHash: string;
  blobSize: number;
  createdAt: string;
  data: string | null; // base64-encoded encrypted blob
}

// ─── Envelope encryption key material ─────────────────────────────────────────
// The ledger is encrypted with a random 32-byte Data Encryption Key (DEK). The
// DEK is wrapped two independent ways and both wraps are stored server-side:
//   - wrappedByPassword: AES-GCM under KEK = Argon2id(password, wrapSalt)
//   - wrappedByMnemonic: AES-GCM under KEK = HKDF(mnemonic entropy)  (recovery)
// The server never sees the DEK, the password, or the mnemonic. Rotating the DEK
// (re-encrypting the log) or the password (re-wrapping) never touches the other.

// A single AES-GCM wrap of the DEK. `wrapSalt` is present only on the
// password-wrapped variant (Argon2id needs a salt); the mnemonic-wrapped variant
// derives its KEK from high-entropy mnemonic bytes via salt-less HKDF.
export interface WrappedDek {
  wrapSalt?: string;   // hex, 32 bytes — Argon2id salt (password variant only)
  iv: string;          // hex, 12 bytes — AES-GCM nonce
  ciphertext: string;  // hex — AES-GCM(DEK) with tag appended
}

export interface Argon2idParams {
  t: number; // time cost (iterations)
  m: number; // memory cost in KiB
  p: number; // parallelism
}

// The bundle stored on the server per user (plus a separate bcrypt recovery_hash).
export interface KeyMaterialBundle {
  v: 1;
  kdf: 'argon2id';
  kdfParams: Argon2idParams;
  wrappedByPassword: WrappedDek; // has wrapSalt
  wrappedByMnemonic: WrappedDek; // no wrapSalt
  // The mnemonic words encrypted UNDER the DEK (not the password). Lets an
  // authenticated user re-view their recovery phrase without the server ever
  // holding a password-crackable copy of it. Decryptable only with the DEK.
  mnemonicSelfBlob?: { iv: string; ciphertext: string };
}

// Worker message types — all key material crosses the boundary as raw bytes
// (ArrayBuffer) or hex strings; the worker never returns the password/mnemonic.
export type CryptoWorkerRequest =
  | {
      type: 'GENERATE_DEK';
      id: string;
    }
  | {
      type: 'ENCRYPT';
      id: string;
      events: LedgerEvent[];
      keyData: ArrayBuffer; // the DEK
    }
  | {
      type: 'DECRYPT';
      id: string;
      blob: number[]; // Uint8Array serialized as array
      keyData: ArrayBuffer; // the DEK
    }
  | {
      type: 'DERIVE_AUTH_TOKEN';
      id: string;
      password: string;
      username: string;
    }
  | {
      // Wrap the DEK under Argon2id(password, wrapSalt).
      type: 'WRAP_DEK';
      id: string;
      dekData: ArrayBuffer;
      password: string;
      wrapSalt: number[];
      params: Argon2idParams;
    }
  | {
      // Unwrap the DEK using Argon2id(password, blob.wrapSalt).
      type: 'UNWRAP_DEK';
      id: string;
      blob: WrappedDek;
      password: string;
      params: Argon2idParams;
    }
  | {
      // Wrap the DEK under HKDF(mnemonic entropy) — the recovery path.
      type: 'WRAP_DEK_MNEMONIC';
      id: string;
      dekData: ArrayBuffer;
      mnemonicEntropy: number[];
    }
  | {
      type: 'UNWRAP_DEK_MNEMONIC';
      id: string;
      blob: WrappedDek;
      mnemonicEntropy: number[];
    }
  | {
      type: 'DERIVE_RECOVERY_PROOF';
      id: string;
      mnemonicEntropy: number[]; // raw entropy bytes, NOT the mnemonic words
      username: string;
    }
  | {
      // AES-GCM encrypt an arbitrary string under a raw key (the DEK).
      type: 'ENCRYPT_STRING';
      id: string;
      plaintext: string;
      keyData: ArrayBuffer;
    }
  | {
      type: 'DECRYPT_STRING';
      id: string;
      blob: { iv: string; ciphertext: string };
      keyData: ArrayBuffer;
    };

export type CryptoWorkerResponse =
  | {
      type: 'GENERATE_DEK_RESULT';
      id: string;
      keyData: ArrayBuffer;
    }
  | {
      type: 'ENCRYPT_RESULT';
      id: string;
      ciphertext: number[]; // Uint8Array serialized as array
    }
  | {
      type: 'DECRYPT_RESULT';
      id: string;
      events: LedgerEvent[];
    }
  | {
      type: 'DERIVE_AUTH_TOKEN_RESULT';
      id: string;
      authToken: string; // 64-char lowercase hex
    }
  | {
      type: 'WRAP_DEK_RESULT';
      id: string;
      iv: string;         // hex, 12 bytes
      ciphertext: string; // hex
    }
  | {
      type: 'UNWRAP_DEK_RESULT';
      id: string;
      keyData: ArrayBuffer; // the DEK
    }
  | {
      type: 'WRAP_DEK_MNEMONIC_RESULT';
      id: string;
      iv: string;         // hex, 12 bytes
      ciphertext: string; // hex
    }
  | {
      type: 'UNWRAP_DEK_MNEMONIC_RESULT';
      id: string;
      keyData: ArrayBuffer; // the DEK
    }
  | {
      type: 'DERIVE_RECOVERY_PROOF_RESULT';
      id: string;
      proof: string; // 64-char lowercase hex
    }
  | {
      type: 'ENCRYPT_STRING_RESULT';
      id: string;
      iv: string;
      ciphertext: string;
    }
  | {
      type: 'DECRYPT_STRING_RESULT';
      id: string;
      plaintext: string;
    }
  | {
      type: 'ERROR';
      id: string;
      message: string;
    };
